import { useContext, useEffect, useRef, useState } from 'react';
import LayersContext from '../../context/LayersContext';
import { MoreVertical, SlidersHorizontal } from 'lucide-react';
import alertSvg from '../../assets/general/metric-metadata-icon-alert.svg';
import ItemsTable from './ItemsTable';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { UnfoldVertical, FoldVertical, ChevronDown } from 'lucide-react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { clearProgrammaticLayers, toggleProgrammaticLayer } from '../../utils/messages';
import TableSkeleton from '../skeletons/TableSkeleton';

interface LayerPanelProps {
    sharedView?: boolean;
    dashboardView: string;
    onPrintClick?: () => void;
    expandedSection?: "metrics" | "layerPanel" | null;
    toggleSize?: () => void;
}

const LayerPanel: React.FC<LayerPanelProps> = ({ dashboardView, expandedSection, toggleSize }) => {
    const { layers, loading, refreshLayers } = useContext(LayersContext);

    // Layer Panel State
    const initialLoadCompleteRef = useRef(false);
    const [activeLayer, setActiveLayer] = useState<string>('');
    // @ts-ignore
    const [searchQuery, setSearchQuery] = useState<string>('');

    // Selected Category State for ItemsTable prop
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [layerDropdownOpen, setLayerDropdownOpen] = useState(false);
    
    // Refs for click handler events
    const dropdownRef = useRef<HTMLDivElement>(null);
    const mobileMenuRef = useRef<HTMLDivElement>(null);
    const layerDropdownRef= useRef<HTMLDivElement>(null);

    // Get the layer items from the active layer
    const activeLayerData = layers?.find(layer => layer.layer_name === activeLayer);
    const layerItems = activeLayerData?.items || [];

    // Get summary info for the current layer
    const summary = activeLayerData?.summary?.length ? activeLayerData.summary[0] : null;

    // Update activeLayer when layers become available
    useEffect(() => {
        if (!activeLayer && layers.length > 0) {
            setActiveLayer(layers[0].layer_name);
        }
    }, [layers]);

    useEffect(() => {
        if (!loading && layers.length > 0) {
            initialLoadCompleteRef.current = true;
        }
    }, [loading, layers]);

    // Reset selected category when layerId changes
    useEffect(() => {
        if (!activeLayerData?.layer_id || !activeLayerData?.layer_name) return;

        // Skip on initial load
        if (!initialLoadCompleteRef.current) return;

        // Reset selected category
        setSelectedCategory(null);

        // Clear current programmatic layers
        clearProgrammaticLayers();

        // Toggle new programmatic layer
        toggleProgrammaticLayer(activeLayerData.layer_id, activeLayerData.layer_name);
    }, [activeLayerData?.layer_id]);

    useClickOutside(dropdownRef, () => setShowCategoryDropdown(false), showCategoryDropdown);
    useClickOutside(mobileMenuRef, () => setShowMobileMenu(false), showMobileMenu);
    useClickOutside(layerDropdownRef, () => setLayerDropdownOpen(false), layerDropdownOpen);

    // Handle active layer change from dropdown
    const handleLayerChange = (layerName: string) => {
        // Update activeLayer
        setActiveLayer(layerName);

        // Refresh layers
        refreshLayers();
    };

    const searchedLayerItems = layerItems?.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Sort the filtered items by activity_date (descending)
    const sortedLayerItems = searchedLayerItems?.sort((a, b) => {
        const dateA = new Date(a.activity_date);
        const dateB = new Date(b.activity_date);
        return dateB.getTime() - dateA.getTime();
    });

    // Calculate the total value of the filtered items
    // const totalValue = filteredLayerItems?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;

    // Paginate the filtered items
    // const totalItems = filteredLayerItems?.length || 0;

    // Get unique categories from layerItems for the dropdown
    const categories = Array.from(
        new Set(
            (layerItems || [])
            .map(item => item.category)
            .filter(Boolean)
        )
    );

    // const isReady = activeLayer && activeLayerData && !loading;

    const noItems =
    !loading &&
    layerItems.length === 0 &&
    layers.length > 0 &&
    !!activeLayerData;

    if (loading) {
        return (
            <div className='space-y-2'>
            <Skeleton width="100%" height={28} />
            <TableSkeleton/>
            </div>
        )
    }

    // Logic for if there are no items present in the selected layer
    if (!loading && layerItems.length === 0 && layers.length === 0) {
        return (
        <div className="flex flex-col items-start w-full p-6 pt-0">
            <div className="w-full border border-gray-200 bg-gray-50 rounded-xl p-4 shadow-sm flex items-center space-x-3">
                <img src={alertSvg} alt='Alert icon' className='w-3 h-3'/>
                <p className="text-sm text-port-text">
                    There are no active layers or items for the selected organization.
                </p>
            </div>
        </div>
        );
    }

    return (
        <>
            {/* Header */}
            <div className="sticky top-0 z-10 py-2 bg-white">
            {layers[0]?.layer_name ? (
                <header className="bg-dark-gg px-2 py-1 flex flex-wrap items-center justify-between gap-2 sm:flex-nowrap sm:justify-center relative">
                    {/* Title */}
                    {layers.length > 1 ? (
                    <div className="relative inline-block w-full tiny:w-auto" ref={layerDropdownRef}>
                        <button
                            onClick={() => setLayerDropdownOpen((prev) => !prev)}
                            className="text-sm font-bold bg-dark-gg text-white px-3 py-1 w-full tiny:w-auto flex items-center justify-between rounded shadow"
                            aria-haspopup="listbox"
                            aria-expanded={layerDropdownOpen}
                        >
                            {activeLayer}
                            <ChevronDown className="ml-2 w-4 h-4" />
                        </button>
                        {layerDropdownOpen && (
                        <ul
                            className="absolute z-10 mt-1 bg-white border border-gray-200 rounded shadow-md w-full tiny:min-w-[16rem] max-h-60 overflow-y-auto text-xs"
                            role="listbox"
                        >
                            {layers.map((layer) => (
                                <li
                                    key={layer.layer_name}
                                    role="option"
                                    aria-selected={activeLayer === layer.layer_name}
                                    onClick={() => {
                                    handleLayerChange(layer.layer_name);
                                    setLayerDropdownOpen(false);
                                    }}
                                    className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                                    activeLayer === layer.layer_name ? "bg-gray-200 font-semibold" : ""
                                    }`}
                                >
                                    {layer.layer_name}
                                </li>
                            ))}
                        </ul>
                        )}
                    </div>
                    ) : (
                        <h2 className="text-sm font-semibold text-white text-start w-full tiny:w-auto">
                            {layers[0]?.layer_name ?? 'Loading'}
                        </h2>
                    )}

                    {/* Buttons for tiny and up */}
                    <div className="hidden tiny:flex items-center space-x-2 absolute right-2 top-1/2 -translate-y-1/2">
                        <button
                            onClick={() => setShowCategoryDropdown((prev) => !prev)}
                            className="text-white hover:bg-white/20 transition-colors"
                            title="Filter by category"
                        >
                            <SlidersHorizontal className="w-5 h-5" />
                        </button>
                        {expandedSection &&
                        <button
                            onClick={toggleSize}
                            className="text-white hover:bg-white/20 rounded-full transition-colors"
                            title={expandedSection === 'layerPanel' ? 'Collapse' : 'Expand'}
                        >
                            {expandedSection === 'layerPanel' ? (
                                <FoldVertical className="w-5 h-5" />
                            ) : (
                                <UnfoldVertical className="w-5 h-5" />
                            )}
                        </button>
                        }
                    </div>
                    
                    {/* Mobile "More" menu trigger */}
                    <div ref={mobileMenuRef} className="tiny:hidden absolute right-2 mt-1">
                        <button
                            onClick={() => setShowMobileMenu((prev) => !prev)}
                            className="text-white hover:bg-white/20 rounded-full transition-colors"
                            title="More options"
                        >
                            <MoreVertical className="w-5 h-5" />
                        </button>

                        {/* Mobile dropdown menu */}
                        {showMobileMenu && (
                        <div className="absolute right-0 bg-white border rounded shadow-lg text-sm z-20 w-40">
                            <button
                                onClick={() => {
                                    setShowCategoryDropdown(true);
                                    setShowMobileMenu(false);
                                }}
                                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                            >
                                Filter by Category
                            </button>
                            <button
                                onClick={() => {
                                    toggleSize?.();
                                    setShowMobileMenu(false);
                                }}
                                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                            >
                                {expandedSection === 'layerPanel' ? 'Collapse' : 'Expand'}
                            </button>
                        </div>
                        )}
                    </div>

                    {/* Filter dropdown */}
                    {showCategoryDropdown && (
                        <div ref={dropdownRef} className="absolute right-10 top-full mt-1 bg-white border rounded shadow-lg text-xs z-20">
                            <div
                                className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                selectedCategory === null ? 'font-bold' : ''
                                }`}
                                onClick={() => {
                                    setSelectedCategory(null);
                                    setShowCategoryDropdown(false);
                                }}
                            >
                                All Categories
                            </div>
                            {categories.map((cat) => (
                                <div
                                    key={cat}
                                    className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                    selectedCategory === cat ? 'font-bold' : ''
                                    }`}
                                    onClick={() => {
                                        setSelectedCategory(cat);
                                        setShowCategoryDropdown(false);
                                    }}
                                >
                                    {cat}
                                </div>
                            ))}
                        </div>
                    )}
                </header>
            ) : (
                <Skeleton width="100%" height={28} />
            )}
            </div>

            {/* Totals Summary */}
            {/* <div className="flex flex-wrap items-center justify-between mb-6 gap-6 p-3 bg-gray-50 border border-gray-200 rounded-md shadow-sm">
                {totalItems > 0 && (
                    <div className="flex items-baseline space-x-1">
                        <p className="text-2xl font-bold text-port-blue font-barlow-condensed leading-none">
                            {formatValue(totalItems, 'integer')}
                        </p>
                        <p className="text-xs font-semibold text-port-text">Total Items</p>
                    </div>
                )}

                {totalValue > 0 && (
                    <div className="flex items-baseline space-x-1">
                        <p className="text-2xl font-bold text-port-blue font-barlow-condensed leading-none">
                            {formatValue(totalValue, summary?.unit ?? "integer")}
                        </p>
                        <p className="text-xs font-semibold text-port-text mt-1">Total</p>
                        {summary?.unit_descriptor && (
                            <p className="text-xs font-semibold text-port-text">{summary.unit_descriptor}</p>
                        )}
                    </div>
                )}
            </div> */}
            
            <div className="grid grid-cols-3 px-6 py-0 w-full">
                {/* Text for no categories or items present */}
                {noItems &&
                <>
                    <p className='col-span-3 mt-4 m-auto text-xs text-port-text'>There are no items from <strong>{activeLayer}</strong> within your current selection.</p>
                    <p className='col-span-3 mt-2 m-auto text-xs text-port-text'>The map is still fully interactive—try widening, moving, or clearing your selection to uncover nearby items.</p>
                </>
                }
            </div>

            {/* ITEMS DISPLAY */}
            <div className='w-full grid grid-cols-3 pb-6'>
                <div className='col-span-3'>
                    <ItemsTable
                        items={sortedLayerItems ?? []} 
                        summary={summary}
                        layerId={activeLayerData?.layer_id ?? null}
                        dashboardView={dashboardView}
                        loading={loading}
                        selectedCategory={selectedCategory}
                        expandedSection={expandedSection} 
                    />
                </div>
            </div>
        </>      
    );
};

export default LayerPanel;